// src/components/admin/RecibosPanel.jsx
import { useEffect, useState } from "react";
import Button from "./Button";
import Select from "./Select";
import TextInput from "./TextInput";
import FieldLabel from "./FieldLabel";
import EstadoReciboSelect from "./EstadoReciboSelect";
import { API, fetchJson } from "../Utiles/api";
import { exportRowsToExcel } from "../Utiles/excel";
import { allowOnlyDigits, fmtMoney, toColegiadoCodigo } from "../Utiles/helpers";
import { Filter, Download, Loader2 } from "lucide-react";

const RecibosPanel = ({ reloadKey, toast, setToast }) => {
  const [estado, setEstado] = useState("TODOS");
  const [colegiado, setColegiado] = useState("");
  const [remesaId, setRemesaId] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [colegiadoBasics, setColegiadoBasics] = useState({}); // { [codigo]: { nombreCompleto, nif } }

  const buildNombreCompleto = (src = {}) => {
    const nombre = src.nombre ?? src.colegiadoNombre ?? src.nombreCompleto ?? src.nombre_completo ?? "";
    const a1 = src.apellido_1 ?? src.apellido1 ?? src.primer_apellido ?? "";
    const a2 = src.apellido_2 ?? src.apellido2 ?? src.segundo_apellido ?? "";
    return [nombre, a1, a2].map((s) => String(s || "").trim()).filter(Boolean).join(" ");
  };
  const pickNif = (src = {}) => src.nif ?? src.NIF ?? src.nifNorm ?? src.nif_norm ?? "";

  const enrichBasics = async (list) => {
    const codes = Array.from(
      new Set((list || []).map((r) => toColegiadoCodigo(r) ?? r.colegiado_codigo ?? r.colegiadoCodigo).filter(Boolean))
    );
    const missing = codes.filter((c) => !colegiadoBasics[c]);
    if (missing.length === 0) return;

    const results = await Promise.all(
      missing.map(async (code) => {
        try {
          let src;
          try {
            src = await fetchJson(API.colegiadoBasics(String(code)));
          } catch {
            src = await fetchJson(API.colegiadoHist(String(code)));
          }
          const nombreCompleto = buildNombreCompleto(src) || "—";
          const nif = pickNif(src) || "—";
          return [code, { nombreCompleto, nif }];
        } catch {
          return [code, { nombreCompleto: "—", nif: "—" }];
        }
      })
    );

    setColegiadoBasics((prev) => {
      const next = { ...prev };
      for (const [code, val] of results) next[code] = val;
      return next;
    });
  };

  const fetchRecibos = async () => {
    try {
      setLoading(true);
      const base = API.buscarRecibos || "/api/recibos";
      const qs = new URLSearchParams();
      const est = (estado || "").trim();
      if (est && est !== "TODOS") qs.set("resultadoBanco", est);
      if (String(colegiado).trim()) qs.set("colegiado", String(colegiado).trim());
      if (String(remesaId).trim()) qs.set("remesaId", String(remesaId).trim());
      const url = qs.toString() ? `${base}?${qs.toString()}` : base;
      const data = await fetchJson(url);
      const arr = Array.isArray(data) ? data : [];
      setRows(arr);
      setSel(new Set());
      await enrichBasics(arr);
    } catch (e) {
      setToast(e.message || "Error cargando recibos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecibos(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetchRecibos(); /* eslint-disable-next-line */ }, [reloadKey]);

  const [sel, setSel] = useState(new Set());
  const allSelected = rows.length > 0 && sel.size === rows.length;
  const toggleAll = () => setSel((s) => (s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  const toggleOne = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const setResultado = async (recibo, valorUi) => {
    try {
      await fetchJson(`/api/recibos/${recibo.id}/resultado?valor=${encodeURIComponent(valorUi)}`, { method: "PATCH" });
      setRows((arr) => arr.map((r) => (r.id === recibo.id ? { ...r, resultado_banco: valorUi } : r)));
      setToast(`Recibo #${recibo.id} → ${valorUi}`);
    } catch (e) {
      setToast(e.message || "Error actualizando estado");
    }
  };

  const setRemesable = async (recibo, val) => {
    try {
      await fetchJson(`/api/recibos/${recibo.id}/remesable`, { method: "PATCH", body: JSON.stringify({ remesable: val }) });
      setRows((arr) => arr.map((r) => (r.id === recibo.id ? { ...r, remesable: val } : r)));
      setToast(`Recibo #${recibo.id} → remesable=${val ? "sí" : "no"}`);
    } catch (e) {
      setToast(e.message || "Error actualizando remesable");
    }
  };

  const doBulkResultadoIds = async (valor) => {
    if (sel.size === 0) return setToast("Selecciona al menos un recibo");
    try {
      await fetchJson(API.bulkResultadoIds, { method: "PATCH", body: JSON.stringify({ valor, ids: Array.from(sel) }) });
      setToast(`Aplicado estado=${valor} a ${sel.size} recibos`);
      await fetchRecibos();
    } catch (e) {
      setToast(e.message || "Error en edición masiva");
    }
  };

  const doBulkRemesableIds = async (remesable) => {
    if (sel.size === 0) return setToast("Selecciona al menos un recibo");
    try {
      await fetchJson(API.bulkRemesableIds, { method: "PATCH", body: JSON.stringify({ remesable, ids: Array.from(sel) }) });
      setToast(`Aplicado remesable=${remesable ? "sí" : "no"} a ${sel.size} recibos`);
      await fetchRecibos();
    } catch (e) {
      setToast(e.message || "Error en edición masiva");
    }
  };

  const exportarRecibos = () => {
    exportRowsToExcel({
      filename: "recibos.xlsx",
      rows,
      columns: [
        { header: "ID", value: (r) => r.id },
        { header: "Colegiado", value: (r) => r.colegiado_codigo ?? r.colegiadoCodigo ?? "" },
        {
          header: "Nombre",
          value: (r) => {
            const code = toColegiadoCodigo(r) ?? r.colegiado_codigo ?? r.colegiadoCodigo;
            return (code && colegiadoBasics[code]?.nombreCompleto) || "";
          },
        },
        { header: "Periodo", value: (r) => r.periodo ?? "" },
        { header: "Importe", value: (r) => r.importe ?? "" },
        { header: "Estado", value: (r) => r.resultado_banco ?? r.resultadoBanco ?? "" },
        { header: "Remesable", value: (r) => (!!r.remesable ? "Sí" : "No") },
        { header: "Mandato", value: (r) => r.mndt_id ?? r.mndtId ?? "" },
        { header: "E2E", value: (r) => r.end_to_end_id ?? r.endToEndId ?? "" },
      ],
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow p-4 space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <FieldLabel label="Estado" />
          <Select value={estado} onChange={(e) => setEstado(e.target.value)} className="w-48">
            <option value="PENDIENTE">Pendientes</option>
            <option value="PENDIENTE_DEVUELTO">Pendiente devuelto</option>
            <option value="ANULADO">Anulado</option>
            <option value="TODOS">Todos</option>
          </Select>
        </div>
        <div>
          <FieldLabel label="Cód. colegiado (opcional)" />
          <TextInput className="w-40" value={colegiado} onChange={(e) => setColegiado(e.target.value)} placeholder="1234" onKeyDown={allowOnlyDigits} inputMode="numeric" />
        </div>
        <div>
          <FieldLabel label="Remesa ID (opcional)" />
          <TextInput className="w-40" value={remesaId} onChange={(e) => setRemesaId(e.target.value)} placeholder="5678" onKeyDown={allowOnlyDigits} inputMode="numeric" />
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={fetchRecibos} iconLeft={loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />}>Cargar</Button>
          <Button variant="outline" iconLeft={<Download size={16} />} onClick={exportarRecibos}>Exportar</Button>
        </div>
      </div>

      {/* Acciones masivas */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => doBulkResultadoIds("COBRADO")}>COBRADO (sel.)</Button>
        <Button onClick={() => doBulkResultadoIds("PENDIENTE")} variant="subtle">PENDIENTE (sel.)</Button>
        <Button onClick={() => doBulkResultadoIds("PENDIENTE_DEVUELTO")} variant="outline">PEND_DEVUELTO (sel.)</Button>
        <span className="mx-2" />
        <Button onClick={() => doBulkRemesableIds(true)} variant="outline">Remesable SÍ (sel.)</Button>
        <Button onClick={() => doBulkRemesableIds(false)} variant="outline">Remesable NO (sel.)</Button>
      </div>

      {/* Tabla */}
      <div className="overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              <th>ID</th><th>Colegiado / Nombre</th><th>Periodo</th>
              <th className="text-left">Importe</th><th>Estado</th><th>Remesable</th><th>Mandato</th><th>E2E</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="py-8 text-center text-gray-500">Cargando…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="py-8 text-center text-gray-400">Sin resultados</td></tr>
            )}
            {rows.map((r) => {
              const id = r.id;
              const seleccionado = sel.has(id);
              const estadoVal = r.resultado_banco ?? r.resultadoBanco ?? "";
              const code = toColegiadoCodigo(r) ?? r.colegiado_codigo ?? r.colegiadoCodigo ?? "";
              const basics = code ? colegiadoBasics[code] : null;
              const nombreUi = basics?.nombreCompleto || "—";

              return (
                <tr key={id} className="border-t">
                  <td><input type="checkbox" checked={seleccionado} onChange={() => toggleOne(id)} /></td>
                  <td>{id}</td>
                  <td>
                    <div className="flex flex-col">
                      <span>{code || "-"}</span>
                      <span className="text-xs text-gray-500">{code ? nombreUi : "-"}</span>
                    </div>
                  </td>
                  <td>{r.periodo}</td>
                  <td className="text-left">{fmtMoney(r.importe)}</td>
                  <td>
                    <EstadoReciboSelect value={estadoVal} onChange={(ui) => setResultado(r, ui)} />
                  </td>
                  <td>
                    <input type="checkbox" checked={!!r.remesable} onChange={(e) => setRemesable(r, e.target.checked)} />
                  </td>
                  <td className="font-mono text-xs">{r.mndt_id ?? r.mndtId ?? ""}</td>
                  <td className="font-mono text-xs">{r.end_to_end_id ?? r.endToEndId ?? ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecibosPanel;
